import { useNavigate } from "@tanstack/react-router";
import { AlertCircle } from "lucide-react";
import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { useCan } from "@/shared/auth";
import { EmptyState } from "@/shared/components/EmptyState";
import { Spinner } from "@/shared/ui/spinner";

import { useOrder } from "../api/use-order";
import { OrderDetailSections } from "../components/OrderDetailSections";

interface OrderDetailPageProps {
  saleOrderId: string;
  /** Forces the fetch to resolve this EXACT sibling package via GET /v1/orders/{id}, bypassing the by-sale-order-id fan-out. Set by a RelatedOrdersBanner link. */
  orderId?: string;
}

export function OrderDetailPage({ saleOrderId, orderId }: OrderDetailPageProps): JSX.Element {
  const { t } = useTranslation("orders");
  const navigate = useNavigate();
  const canWrite = useCan("orders:write");

  const { data: order, isLoading, isError } = useOrder(saleOrderId, orderId);

  function handleBack(): void {
    void navigate({ to: "/orders" });
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="order-detail-loading">
        <Spinner size="lg" label={t("loading_orders")} />
      </div>
    );
  }

  if (isError || !order) {
    return (
      <EmptyState
        icon={<AlertCircle className="h-6 w-6" />}
        title={t("empty_order_not_found")}
        body={t("empty_order_not_found_desc")}
      />
    );
  }

  return <OrderDetailSections order={order} onBack={handleBack} canWrite={canWrite} />;
}
